import NiceModal, { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import Popup from '@shared/components/Popup/Popup';
import Select from '@shared/components/Select/Select';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpirationPreset, formatExpiryDate, resolveExpiresAt } from 'api/meedioBackend/apiKeys/constants';
import { rotateApiKey } from 'api/meedioBackend/apiKeys/rotateApiKey';
import { RotateApiKeyRequest } from 'api/meedioBackend/apiKeys/types';
import { ReactComponent as Check } from 'assets/icons/Check.svg';
import { ReactComponent as RotateClockwise } from 'assets/icons/RotateClockwise.svg';
import { ReactComponent as X } from 'assets/icons/X.svg';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import testingConstants from 'utils/testingConstants';

import ApiKeyActionErrorModal from './ApiKeyActionErrorModal';
import ApiKeyCreatedModal from './ApiKeyCreatedModal';
import { API_KEYS_MODAL_WRAPPER_ID } from './constants';

/** `keep` inherits the previous expiry (omit body field); the rest override it (AC-05-02). */
type RotateExpiration = 'keep' | ExpirationPreset;

const ROTATE_EXPIRATIONS: RotateExpiration[] = ['keep', 'week', 'month', 'quarter', 'half_year', 'no_expiration'];
const ROTATE_LABEL_KEYS: Record<RotateExpiration, string> = {
  keep: 'workspaces_settings.api_keys.rotate.expiration_keep',
  week: 'workspaces_settings.api_keys.create.expiration_week',
  month: 'workspaces_settings.api_keys.create.expiration_month',
  quarter: 'workspaces_settings.api_keys.create.expiration_quarter',
  half_year: 'workspaces_settings.api_keys.create.expiration_half_year',
  no_expiration: 'workspaces_settings.api_keys.create.expiration_none',
};

const ROTATE_BULLETS: { key: string; tone: 'negative' | 'positive' }[] = [
  { key: 'workspaces_settings.api_keys.rotate.bullet_invalidated', tone: 'negative' },
  { key: 'workspaces_settings.api_keys.rotate.bullet_sessions_stop', tone: 'negative' },
  { key: 'workspaces_settings.api_keys.rotate.bullet_name_scopes', tone: 'positive' },
  { key: 'workspaces_settings.api_keys.rotate.bullet_expiration', tone: 'positive' },
];

interface RotateApiKeyModalProps {
  /** Backend workspace UUID (already resolved by the caller). */
  workspaceUuid: string;
  /** The key being rotated. */
  keyId: string;
  /** The key's current expiry (`null` = no expiration). Shown so the user knows the live TTL. */
  currentExpiresAt?: string | null;
  /** Invoked after the user finishes the Created modal so the caller refreshes the list (AC-05-03). */
  onRotated?: () => void;
}

/**
 * Rotate modal (Figma 6392:67662): warns the old secret is invalidated, offers an optional
 * expiry override, and on 201 hands the NEW one-time secret to `ApiKeyCreatedModal`.
 * Failure opens the shared action-error modal whose Try again re-runs this rotate (AC-05-05).
 */
const RotateApiKeyModal = create<RotateApiKeyModalProps>(({ workspaceUuid, keyId, currentExpiresAt, onRotated }) => {
  const { t } = useTranslation();
  const { remove } = useModal(RotateApiKeyModal);

  const [expiration, setExpiration] = useState<RotateExpiration>('keep');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const expiresAtPreview = useMemo(() => {
    if (expiration === 'keep' || expiration === 'no_expiration') return null;
    return formatExpiryDate(resolveExpiresAt(expiration) as string);
  }, [expiration]);

  // When keeping the current expiry, surface the live TTL so the user isn't guessing (review feedback).
  const keepExpiryHelper =
    currentExpiresAt === undefined
      ? null
      : currentExpiresAt
      ? t('workspaces_settings.api_keys.create.expires_on', { date: formatExpiryDate(currentExpiresAt) })
      : t('workspaces_settings.api_keys.no_expiration');

  const handleRotate = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    // `keep` omits the body field entirely so the backend inherits the previous expiry (AC-05-02).
    const payload: RotateApiKeyRequest | undefined =
      expiration === 'keep' ? undefined : { expires_at: resolveExpiresAt(expiration) };

    rotateApiKey(workspaceUuid, keyId, payload)
      .then((response) => {
        remove();
        void NiceModal.show(ApiKeyCreatedModal, { apiKey: response.key, onFinish: onRotated, variant: 'rotate' });
      })
      .catch(() => {
        void NiceModal.show(ApiKeyActionErrorModal, { operation: 'rotate', onRetry: handleRotate });
      })
      .finally(() => setIsSubmitting(false));
  };

  const expirationOptions: DropdownSelectionProps[] = ROTATE_EXPIRATIONS.map((value) => ({
    title: t(ROTATE_LABEL_KEYS[value]),
    onClick: () => setExpiration(value),
    isSelected: expiration === value,
  }));

  return (
    <Popup isVisible closePopup={remove} isViewportScrollable wrapperId={API_KEYS_MODAL_WRAPPER_ID}>
      <Popup.Container
        className="shadow-modal border-gray-30 relative border !bg-gray-10 !max-w-[calc(100vw-2rem)] md:w-[512px] md:!max-w-[512px]"
        dataTestId={testingConstants.rotateApiKeyModal}
      >
        <button
          type="button"
          onClick={remove}
          aria-label={t('close')}
          className="hover:bg-gray-30 absolute right-4 top-3.5 z-10 flex h-8 w-8 items-center justify-center rounded-xl p-2.5"
        >
          <X className="stroke-grayscale-black h-5 w-5 stroke-2" />
        </button>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-6 pb-4 pt-12">
          <div className="flex flex-col items-center gap-6 md:gap-8">
            <GradientIconWithShadow icon={RotateClockwise} iconVariant="primary" />
            <div className="flex w-full flex-col items-center gap-2 text-center">
              <span className="text-grayscale-black text-xl font-semibold leading-tight">
                {t('workspaces_settings.api_keys.rotate.title')}
              </span>
              <span className="text-grayscale-black text-size-md font-light">
                {t('workspaces_settings.api_keys.rotate.subtitle')}
              </span>
            </div>
          </div>

          <div className="mt-6 flex w-full flex-col gap-4">
            <div className="border-gray-30 flex w-full flex-col gap-1 rounded-2xl border bg-white p-4">
              {ROTATE_BULLETS.map(({ key, tone }) => (
                <div key={key} className="flex items-start gap-2">
                  {tone === 'positive' ? (
                    <Check className="text-primary-50 mt-0.5 h-5 w-5 shrink-0 stroke-current stroke-2" />
                  ) : (
                    <X className="text-tertiary-50 mt-0.5 h-5 w-5 shrink-0 stroke-current stroke-2" />
                  )}
                  <span className="text-grayscale-black text-size-sm text-left font-light">{t(key)}</span>
                </div>
              ))}
            </div>

            <div className="flex w-full flex-col gap-2">
              <span className="text-grayscale-black text-size-sm font-medium">
                {t('workspaces_settings.api_keys.rotate.expiration_label')}
              </span>
              <Select size="md" options={expirationOptions} dataTestId={testingConstants.rotateApiKeyExpirationSelect}>
                {t(ROTATE_LABEL_KEYS[expiration])}
              </Select>
              {expiration === 'keep'
                ? keepExpiryHelper && <span className="text-gray-80 text-size-sm font-light">{keepExpiryHelper}</span>
                : expiresAtPreview && (
                    <span className="text-grayscale-black text-size-sm font-light">
                      {t('workspaces_settings.api_keys.create.expires_on', { date: expiresAtPreview })}
                    </span>
                  )}
            </div>
          </div>
        </div>

        <div className="border-gray-30 shrink-0 border-t px-6 py-4">
          <div className="flex justify-end gap-2">
            <Button variant="secondaryTertiary" size="md" onClick={remove}>
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleRotate}
              loading={isSubmitting}
              data-testid={testingConstants.rotateApiKeySubmitButton}
            >
              {t('workspaces_settings.api_keys.rotate.submit')}
            </Button>
          </div>
        </div>
      </Popup.Container>
    </Popup>
  );
});

export default RotateApiKeyModal;
